// src/components/PurchaseFlow/Steps/ConfirmationViews/Variant3SuccessView.js
// Variante POS: confirmación + QR Mi Cuenta + Copiar/WhatsApp + Imprimir contrato
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faClock,
  faUserCheck,
  faReceipt,
  faCopy,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import QRCode from 'qrcode';
import { getSaleResultType, RESULT_TYPES } from './saleResultTypeUtils';
import ActivationPendingSuccess from './ActivationPendingSuccess';

const QR_SIZE = 240;
const LINK_TRUNCATE_LEN = 40;

const RESULT_UI_CONFIG = {
  [RESULT_TYPES.AUTO_APPROVED]: {
    icon: faCheck,
    title: '¡Préstamo aprobado!',
    subtitle: 'Ya podés continuar con la entrega y compartir Mi Cuenta con el cliente.',
    alertClassName: null,
    alertText: null
  },
  [RESULT_TYPES.PRE_APPROVED]: {
    icon: faClock,
    title: '¡Prestamo Aprobado!',
    subtitle: null,
    subtitleBullets: true,
    alertClassName: 'alert-warning',
    alertText: null
  },
  [RESULT_TYPES.APPROVED_WITH_OBS]: {
    icon: faUserCheck,
    title: '¡Préstamo aprobado!',
    subtitle: 'Ya podés entregar el producto.',
    alertClassName: 'alert-info',
    alertText: null
  }
};

const Variant3SuccessView = ({
  contracts,
  payment,
  customerMyAccount,
  contractDownloaded,
  confirmationSummary,
  onPrintContract,
  onPrintReceipt,
  onShareMyAccount,
  onViewDetail,
  onViewObservation,
  clientData,
  onCloseToSales,
  activationOutcome = 'completed'
}) => {
  const [copyLabel, setCopyLabel] = useState('Copiar link');

  const myAccountUrl = customerMyAccount?.my_account_url || clientData?.my_account_url || '';
  const clientName = clientData?.firstName || '';
  const saleResultType = getSaleResultType(confirmationSummary);
  const resultUi = RESULT_UI_CONFIG[saleResultType] || RESULT_UI_CONFIG[RESULT_TYPES.AUTO_APPROVED];
  const observationDetails = confirmationSummary?.observation_details || '';

  useEffect(() => {
    if (!myAccountUrl) return;
    const canvas = document.getElementById('rg-success-qr-canvas');
    if (!canvas) return;
    QRCode.toCanvas(canvas, myAccountUrl, {
      width: QR_SIZE,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' }
    });
  }, [myAccountUrl]);

  const handleCopyLink = () => {
    if (!myAccountUrl) return;
    navigator.clipboard.writeText(myAccountUrl).then(() => {
      setCopyLabel('Copiado');
      setTimeout(() => setCopyLabel('Copiar link'), 2000);
    });
  };

  const handleSendWhatsApp = () => {
    const text = `Hola ${clientName}, aquí puedes ver toda la información de tus préstamos y realizar gestiones: ${myAccountUrl}`;
    const whatsappText = encodeURIComponent(text);
    const rawPhone = customerMyAccount?.phone?.[0]?.applicant_phone?.phone;
    const phone = rawPhone ? rawPhone.replace(/\D/g, '') : '';
    const whatsappUrl = phone ? `https://wa.me/${phone}?text=${whatsappText}` : `https://wa.me/?text=${whatsappText}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleClose = () => {
    if (typeof onCloseToSales === 'function') {
      onCloseToSales();
      return;
    }
    window.location.href = '/';
  };

  if (activationOutcome === 'pending') {
    return <ActivationPendingSuccess onCloseToSales={onCloseToSales} />;
  }

  const truncatedLink = myAccountUrl.length > LINK_TRUNCATE_LEN
    ? `${myAccountUrl.slice(0, LINK_TRUNCATE_LEN)}…`
    : myAccountUrl;

  return (
    <div className="rg-sale-success-view rg-sale-success-view--variant-qr">
      <div className="rg-loan-success-qr__grid">
        {/* Columna izquierda: Confirmación */}
        <div className="rg-loan-success-qr__confirm">
          <div className="stepper-success-icon">
            <FontAwesomeIcon icon={resultUi.icon} />
          </div>
          <h2 className="rg-sale-step__title rg-sale-step__title--success">
            {resultUi.title}
          </h2>
          {resultUi.subtitleBullets ? (
            <ul className="rg-loan-success-qr__sub-list">
              <li>Un analista se pondrá en contacto con el cliente para completar la operación.</li>
              <li>
                Para ver el estado ingresá a{' '}
                <Link to="/ventas" className="rg-loan-success-qr__sub-link">
                  Ventas
                </Link>
                .
                <ul className="rg-loan-success-qr__sub-list">
                  <li>Una vez que el estado figure aprobado, podrás entregar el producto.</li>
                </ul>
              </li>
            </ul>
          ) : (
            <p className="rg-loan-success-qr__sub">
              {resultUi.subtitle}
            </p>
          )}
          {observationDetails && resultUi.alertClassName && (
            <div className={`alert ${resultUi.alertClassName}`}>
              {observationDetails}
            </div>
          )}
          {resultUi.alertText && (
            <div className={`alert ${resultUi.alertClassName}`}>
              {resultUi.alertText}
            </div>
          )}
          {confirmationSummary?.observation_status === 'open' ? (
            <button
              type="button"
              className="rg-btn rg-btn--outline-primary rg-btn--lg rg-loan-success-qr__receipt"
              onClick={onViewObservation}
              title="Ver la observación pendiente"
            >
              <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
              Ver Observación
            </button>
          ) : payment?.receipt_url ? (
            <button
              type="button"
              className="rg-btn rg-btn--outline-primary rg-btn--lg rg-loan-success-qr__receipt"
              onClick={() => onPrintReceipt(payment.receipt_url)}
              title="Ver o imprimir el comprobante de pago"
            >
              <FontAwesomeIcon icon={faReceipt} className="me-2" />
              Imprimir comprobante
            </button>
          ) : null}
        </div>

        {/* Columna derecha: QR + compartir */}
        <div className="rg-loan-success-qr__share">
          <div className="rg-loan-success-qr__card">
            <h6 className="rg-loan-success-qr__card-title">Acceso a Mi Cuenta</h6>
            {myAccountUrl ? (
              <div className="rg-loan-success-qr__qr-wrap">
                <canvas id="rg-success-qr-canvas" className="rg-loan-success-qr__qr" />
              </div>
            ) : (
              <div className="rg-loan-success-qr__qr-placeholder">Generando QR…</div>
            )}
            {myAccountUrl && (
              <p className="rg-loan-success-qr__link" title={myAccountUrl}>
                {truncatedLink}
              </p>
            )}
            <div className="rg-loan-success-qr__actions">
              <div className="input-group">
                <input
                  type="text"
                  value={myAccountUrl}
                  className="form-control form-control-sm"
                  readOnly
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary copy-button"
                  style={{ border: '1px solid #dce7f1' }}
                  onClick={handleCopyLink}
                  disabled={!myAccountUrl}
                >
                  <FontAwesomeIcon icon={faCopy} className="me-1" />
                  {copyLabel}
                </button>
              </div>
              <button
                type="button"
                className="btn btn-success w-100"
                style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                onClick={handleSendWhatsApp}
                disabled={!myAccountUrl}
              >
                <i className="bi bi-whatsapp me-2" aria-hidden />
                Enviar por WhatsApp
              </button>
            </div>
            <p className="rg-loan-success-qr__microcopy">
              Escaneá o enviá el link al cliente.
            </p>
          </div>
        </div>
      </div>

      {contractDownloaded && (
        <div className="rg-loan-success-qr__nav">
          <button
            type="button"
            className="rg-btn rg-btn--secondary"
            onClick={() => { window.location.href = '/'; }}
          >
            Volver al Inicio
          </button>
          <button
            type="button"
            className="rg-btn rg-btn--primary"
            onClick={onViewDetail}
          >
            Ver detalle de venta
          </button>
        </div>
      )}

      <div className="rg-loan-success-qr__close-wrap">
        <button
          type="button"
          className="rg-btn rg-btn--primary rg-btn--lg rg-loan-success-qr__close"
          onClick={handleClose}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};

export default Variant3SuccessView;
